import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "../api/client";
import { Send, Plus, MessageCircle, Trash2 } from "lucide-react";

export default function AskSopfie() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  const refreshSessions = async () => {
    try {
      const { data } = await api.get("/ai/sessions");
      setSessions(data || []);
      if (!activeId && data && data.length) setActiveId(data[0].id);
    } catch {
      setSessions([]);
    }
  };

  useEffect(() => {
    refreshSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    api.get(`/ai/sessions/${activeId}/messages`)
      .then((r) => setMessages(r.data || []))
      .catch(() => setMessages([]));
  }, [activeId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const newSession = async () => {
    try {
      const { data } = await api.post("/ai/sessions");
      setSessions((s) => [data, ...s]);
      setActiveId(data.id);
      setMessages([]);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const removeSession = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    await api.delete(`/ai/sessions/${id}`);
    setSessions((s) => s.filter((x) => x.id !== id));
    if (activeId === id) {
      setActiveId(null);
      setMessages([]);
    }
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text) return;
    let sid = activeId;
    if (!sid) {
      try {
        const { data } = await api.post("/ai/sessions");
        sid = data.id;
        setSessions((s) => [data, ...s]);
        setActiveId(sid);
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
        return;
      }
    }
    const optimistic = {
      id: `tmp-${Date.now()}`,
      session_id: sid,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setDraft("");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/ai/sessions/${sid}/messages`, { content: text });
      setMessages((m) => [...m, data]);
      // refresh sessions to update title/order
      refreshSessions();
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      const friendly =
        e.response?.status === 502
          ? "Sopfie is taking a breath right now — please try again in a moment."
          : detail || e.message;
      setError(friendly);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-12 gap-5" data-testid="ask-sopfie">
      {/* Sessions list */}
      <aside className="md:col-span-4 lg:col-span-3">
        <button onClick={newSession} className="btn-primary w-full justify-center mb-3" data-testid="ai-new-session">
          <Plus size={14} strokeWidth={1.6} /> New conversation
        </button>
        <div className="space-y-1.5 max-h-[60vh] overflow-auto pr-1" data-testid="ai-sessions-list">
          {sessions.length === 0 && (
            <p className="text-xs text-[color:var(--ink-3)] italic px-1">No conversations yet.</p>
          )}
          {sessions.map((s) => (
            <div
              key={s.id}
              onClick={() => setActiveId(s.id)}
              className={`group flex items-start gap-2 px-3 py-2 rounded-xl cursor-pointer border transition-colors ${
                activeId === s.id
                  ? "bg-[var(--bg-2)] border-[color:var(--accent)]/40"
                  : "border-transparent hover:bg-[var(--bg-2)]/60"
              }`}
              data-testid={`ai-session-${s.id}`}
            >
              <MessageCircle size={14} strokeWidth={1.5} className="text-[color:var(--accent)] mt-1 shrink-0" />
              <p className="flex-1 text-sm text-[color:var(--ink)] truncate">{s.title || "New conversation"}</p>
              <button
                onClick={(e) => { e.stopPropagation(); removeSession(s.id); }}
                className="opacity-0 group-hover:opacity-100 text-[color:var(--ink-3)] hover:text-[color:var(--accent-2)]"
                data-testid={`ai-session-delete-${s.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat */}
      <div className="md:col-span-8 lg:col-span-9 card-soft p-5 md:p-6 flex flex-col" style={{ minHeight: "60vh" }}>
        <div ref={scrollRef} className="flex-1 overflow-auto pr-1 space-y-4" data-testid="ai-messages">
          {messages.length === 0 && !busy && (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <p className="overline mb-3">Ask Sopfie</p>
              <h3 className="font-serif text-3xl text-[color:var(--ink)] tracking-tight max-w-md">
                What is your body asking for today?
              </h3>
              <p className="mt-3 text-sm text-[color:var(--ink-2)] max-w-md">
                Share what you're feeling — physical or emotional — and I'll meet you here, just as I would in person.
              </p>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${m.role}-${m.id}`}>
              <div
                className={`max-w-[78%] px-4 py-3 rounded-2xl text-[15px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[color:var(--ink)] text-[color:var(--bg)] rounded-br-md"
                    : "bg-[var(--bg-2)] text-[color:var(--ink)] rounded-bl-md font-serif text-lg"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start" data-testid="ai-typing">
              <div className="bg-[var(--bg-2)] px-4 py-3 rounded-2xl text-[color:var(--ink-3)] italic font-serif">
                Sopfie is here, taking a breath…
              </div>
            </div>
          )}
        </div>

        {error && (
          <p className="mt-2 text-sm text-[color:var(--accent-2)]" data-testid="ai-error">{error}</p>
        )}

        <form onSubmit={send} className="mt-4 flex gap-2 items-end" data-testid="ai-form">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(e);
              }
            }}
            rows={2}
            placeholder="Speak from where you are…"
            className="input-warm flex-1 resize-none"
            disabled={busy}
            data-testid="ai-input"
          />
          <button type="submit" disabled={busy || !draft.trim()} className="btn-primary !py-3 !px-4" data-testid="ai-send">
            <Send size={16} strokeWidth={1.6} />
          </button>
        </form>
        <p className="mt-2 text-xs text-[color:var(--ink-3)]">
          AI Sopfie is for guidance and presence — not medical advice. For anything urgent, please contact a qualified practitioner.
        </p>
      </div>
    </div>
  );
}
